/**
 * Turns a run's metric rollup into the switch recommendation shown above the
 * report. Kept free of JSX so the thresholds can be unit tested directly.
 */

/** Latency past this much slower is worth a human look before switching. */
export const LATENCY_REGRESSION_PCT = 50;

/** Above this, the candidate changes so much that spot-checking is useless. */
export const HIGH_DRIFT_RATE = 0.5;

export function formatPercent(value) {
  if (!Number.isFinite(value)) {
    return "—";
  }

  return `${Math.round(value * 1000) / 10}%`;
}

export function formatSeconds(milliseconds) {
  if (!Number.isFinite(milliseconds)) {
    return "—";
  }

  return `${Math.round(milliseconds / 100) / 10}s`;
}

export function formatSignedPercent(value) {
  if (!Number.isFinite(value)) {
    return "—";
  }

  return `${value > 0 ? "+" : ""}${value}%`;
}

/**
 * Anything that would change a borrower-facing number blocks the switch;
 * wording-only drift is advisory.
 *
 * @param {Object} metrics - `summary.metrics` from an eval report
 * @returns {{tone: "good"|"warn"|"bad", title: string, reason: string}}
 */
export function buildVerdict(metrics = {}) {
  const { driftRate, highSeverityRate, accuracy, latency } = metrics;
  const blockers = [];

  if (Number.isFinite(highSeverityRate) && highSeverityRate > 0) {
    blockers.push(
      `${formatPercent(highSeverityRate)} of cases moved income or flipped status`,
    );
  }
  if (accuracy?.verdict === "candidate_worse") {
    blockers.push(
      "less accurate than production against human-reviewed income",
    );
  }
  if (latency && latency.deltaPct > LATENCY_REGRESSION_PCT) {
    blockers.push(`${formatSignedPercent(latency.deltaPct)} slower per loan`);
  }

  if (blockers.length) {
    return {
      tone: "bad",
      title: "Review before switching",
      reason: blockers.join(" · "),
    };
  }

  if (driftRate === 0) {
    return {
      tone: "good",
      title: "Identical to production",
      reason: "Every stage produced the same output on both models.",
    };
  }

  if (Number.isFinite(driftRate) && driftRate > HIGH_DRIFT_RATE) {
    return {
      tone: "warn",
      title: "Safe on the numbers, but noisy",
      reason: `${formatPercent(driftRate)} of cases differ in wording without changing income or status.`,
    };
  }

  return {
    tone: "good",
    title: "Safe to switch",
    reason: "No income moves, no status flips, no accuracy regression.",
  };
}
